import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, ExternalLink, FileText, XCircle } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { useLanguage } from "@/core/LanguageContext";
import { getSupplierIncomingDocumentSignedUrl } from "../api";
import type { SupplierIncomingResult } from "../api";

function isImageFile(fileName: string): boolean {
  const ext = fileName.split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "webp", "gif"].includes(ext ?? "");
}

function getStatusBadge(status: string) {
  switch (status.toLowerCase()) {
    case "approved":
      return (
        <Badge className="bg-success text-success-foreground">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Approved
        </Badge>
      );
    case "rejected":
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" />
          Rejected
        </Badge>
      );
    case "pending":
      return (
        <Badge variant="secondary">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

interface InspectionDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inspection: SupplierIncomingResult | null;
}

export function InspectionDetailDialog({
  open,
  onOpenChange,
  inspection,
}: InspectionDetailDialogProps) {
  const { t } = useLanguage();
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isLoadingDocumentUrl, setIsLoadingDocumentUrl] = useState(false);
  const [documentUrlError, setDocumentUrlError] = useState(false);

  useEffect(() => {
    if (!open || !inspection?.document_file_path) {
      setDocumentUrl(null);
      setDocumentUrlError(false);
      return;
    }

    setIsLoadingDocumentUrl(true);
    setDocumentUrlError(false);

    getSupplierIncomingDocumentSignedUrl(inspection.document_file_path)
      .then((url) => {
        setDocumentUrl(url);
      })
      .catch(() => {
        setDocumentUrl(null);
        setDocumentUrlError(true);
      })
      .finally(() => {
        setIsLoadingDocumentUrl(false);
      });
  }, [open, inspection?.document_file_path]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {inspection?.overall_status === "approved" ? (
              <CheckCircle2 className="w-5 h-5 text-success" />
            ) : inspection?.overall_status === "rejected" ? (
              <XCircle className="w-5 h-5 text-destructive" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-muted-foreground" />
            )}
            {t("mod_8d.supplierQuality.incomingInspection.detail.title", "Inspection Details")}
          </DialogTitle>
        </DialogHeader>
        {inspection && (
          <div className="space-y-5">
            {/* Metadata grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("mod_8d.supplierQuality.incomingInspection.detail.material", "Material")}
                </p>
                <p className="font-medium">{inspection.material_name || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("mod_8d.supplierQuality.incomingInspection.detail.batchLot", "Batch/Lot")}
                </p>
                <p className="font-medium">{inspection.batch_lot_number || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("mod_8d.supplierQuality.incomingInspection.detail.supplier", "Supplier")}
                </p>
                <p className="font-medium">{inspection.supplier_name || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("mod_8d.supplierQuality.incomingInspection.detail.createdAt", "Created At")}
                </p>
                <p className="font-medium">
                  {format(new Date(inspection.created_at), "MMM dd, yyyy HH:mm")}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("mod_8d.supplierQuality.incomingInspection.detail.status", "Status")}
                </p>
                <div className="mt-1">{getStatusBadge(inspection.overall_status)}</div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("mod_8d.supplierQuality.incomingInspection.detail.documentType", "Document Type")}
                </p>
                <p className="font-medium">{inspection.document_type?.toUpperCase() || "-"}</p>
              </div>
              {inspection.inspector_notes && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">
                    {t("mod_8d.supplierQuality.incomingInspection.detail.inspectorNotes", "Inspector Notes")}
                  </p>
                  <p className="text-sm mt-0.5">{inspection.inspector_notes}</p>
                </div>
              )}
            </div>

            {/* Document viewer */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">
                  {t("mod_8d.supplierQuality.incomingInspection.detail.document", "Document")}
                </h4>
                {documentUrl && (
                  <div className="flex items-center gap-2">
                    <a
                      href={documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button type="button" variant="ghost" size="sm" className="gap-1.5">
                        <ExternalLink className="h-3.5 w-3.5" />
                        {t("mod_8d.supplierQuality.incomingInspection.detail.openDocument", "Open")}
                      </Button>
                    </a>
                    <a
                      href={documentUrl}
                      download={inspection.document_file_name}
                    >
                      <Button type="button" variant="outline" size="sm" className="gap-1.5">
                        <Download className="h-3.5 w-3.5" />
                        {t("mod_8d.supplierQuality.incomingInspection.detail.downloadDocument", "Download")}
                      </Button>
                    </a>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{inspection.document_file_name}</span>
              </div>

              {isLoadingDocumentUrl ? (
                <div className="h-64 bg-muted/50 rounded-lg animate-pulse flex items-center justify-center">
                  <div className="text-sm text-muted-foreground">
                    {t("mod_8d.supplierQuality.incomingInspection.detail.loadingDocument", "Loading document...")}
                  </div>
                </div>
              ) : documentUrlError ? (
                <div className="flex items-center gap-2 p-4 rounded-lg border border-dashed text-sm text-muted-foreground">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {t(
                      "mod_8d.supplierQuality.incomingInspection.detail.documentError",
                      "Unable to load document preview.",
                    )}
                  </span>
                </div>
              ) : documentUrl ? (
                isImageFile(inspection.document_file_name) ? (
                  <div className="rounded-lg border overflow-hidden bg-muted/20">
                    <img
                      src={documentUrl}
                      alt={inspection.document_file_name}
                      className="max-h-[480px] w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <iframe
                      src={documentUrl}
                      title={inspection.document_file_name}
                      className="w-full h-[480px]"
                    />
                  </div>
                )
              ) : null}
            </div>

            {/* Test results */}
            {inspection.test_results && inspection.test_results.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">
                  {t("mod_8d.supplierQuality.incomingInspection.detail.testResults", "Test Results")}
                </h4>
                <div className="space-y-2">
                  {inspection.test_results.map((result, idx) => (
                    <div key={idx} className="p-3 rounded bg-muted/50 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{result.name}</span>
                        {result.passed !== null && result.passed !== undefined && (
                          <Badge
                            variant={result.passed ? "default" : "destructive"}
                            className="text-xs"
                          >
                            {result.passed
                              ? t("mod_8d.supplierQuality.incomingInspection.detail.pass", "PASS")
                              : t("mod_8d.supplierQuality.incomingInspection.detail.fail", "FAIL")}
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground">
                        {result.value} {result.unit}{" "}
                        {result.specification && `(Nominal: ${result.specification})`}
                        {(result.ucl !== null || result.lcl !== null) && (
                          <span className="ml-2">
                            {result.lcl !== null && `LCL: ${result.lcl}`}
                            {result.lcl !== null && result.ucl !== null && " | "}
                            {result.ucl !== null && `UCL: ${result.ucl}`}
                          </span>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { getStatusBadge };
